import styled from 'styled-components';

import { formatToHoursAndMinutesFromSeconds } from '@/utils/time';
import { MetaLabel } from '@/ui/Label';
import { BadgeLabel } from '@/ui/Label';
import { LONG_DATE_FORMAT, SHORT_DATE_FORMAT, useLocalizedDate } from '@/hooks/useLocalizedDate';
import { RLearningContentLocationData, RLearningType } from '@/store/v2';
import { formatSingleDateDependingOnTimezone, formatTimeDependingOnUserTimezone } from '@/utils/dates';
import { JOURNEY_INSIGHTS_LEARNING_TYPES_OPTIONS } from '@/features/journey/admin/atoms/JourneyInsightsTabsLearningItemsTypeFilter/JourneyInsightsTabsLearningItemsTypeFilter';
import { IconPropType } from '@/ui/Icon/types';
import { camelCaseToUpperUnderscoreCase } from '@/utils/text';

export type LearningCardMetaProps = {
  type?: string;
  level?: string;
  totalEffort?: number;
  totalLearningItems?: number;
  locations?: RLearningContentLocationData[];
  availabilityScheduledAtTimezone?: number;
  timezone?: number;
  systemGenerated?: boolean;
  isOptional?: boolean;
  availabilityScheduledAt?: Date;
};

const RootStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetaWrapStyled = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

export const LearningCardMeta = ({
  totalEffort,
  level,
  locations,
  type,
  systemGenerated,
  isOptional,
  availabilityScheduledAt,
  availabilityScheduledAtTimezone,
}: LearningCardMetaProps): JSX.Element => {
  const effortText = totalEffort || totalEffort === 0 ? formatToHoursAndMinutesFromSeconds(totalEffort) : undefined;

  const liveEventDate =
    type === RLearningType.LiveEvent && !!locations?.[0]?.startDate && !!locations?.[0]?.endDate
      ? formatTimeDependingOnUserTimezone({
          startTime: locations?.[0]?.startDate as Date,
          endTime: locations?.[0]?.endDate as Date,
          formatOptions: {
            showTimezone: true,
            startFormat: 'd MMM, HH:mm',
            endFormat: SHORT_DATE_FORMAT,
          },
        })
      : '';

  const availabilityScheduledAtText = availabilityScheduledAt
    ? `Available on ${formatSingleDateDependingOnTimezone({
        date: availabilityScheduledAt || new Date(),
        formatOptions: {
          showTimezone: true,
          format: 'd MMM, HH:mm',
        },
        timezoneOffsetInMinutes: availabilityScheduledAtTimezone,
      })}`
    : undefined;

  const formattedStart = useLocalizedDate(locations?.[0]?.startDate, LONG_DATE_FORMAT);
  const formattedEnd = useLocalizedDate(locations?.[0]?.endDate, SHORT_DATE_FORMAT);

  const locationDate = formattedStart && formattedEnd && `${formattedStart} - ${formattedEnd}`;

  const displayTypeMeta = JOURNEY_INSIGHTS_LEARNING_TYPES_OPTIONS.find(
    (item) => camelCaseToUpperUnderscoreCase(item.value) === type
  );

  const displayTypeMetaIcon = displayTypeMeta?.prefixIcon;
  const displayTypeMetaText = displayTypeMeta?.displayValue;

  const shouldShowMetaBadges = isOptional || systemGenerated || availabilityScheduledAt;

  return (
    <RootStyled>
      <MetaWrapStyled>
        {type && <MetaLabel icon={displayTypeMetaIcon as IconPropType} text={displayTypeMetaText || type} />}
        {level && <MetaLabel icon={['far', 'signal-strong']} text={level} />}
        {effortText && <MetaLabel icon={['far', 'clock']} text={effortText} />}
        {locationDate && (
          <MetaLabel icon={['far', 'calendar']} text={liveEventDate ? liveEventDate : `${formattedStart} - ${formattedEnd}`} />
        )}
      </MetaWrapStyled>
      {shouldShowMetaBadges && (
        <MetaWrapStyled>
          {systemGenerated && <BadgeLabel badgeVariant="badge-survey" text="Impact survey" noIcon />}
          {isOptional && <BadgeLabel badgeVariant="badge-recommended" text="Optional" noIcon />}
          {availabilityScheduledAtText && <BadgeLabel badgeVariant="badge-text" text={availabilityScheduledAtText} noIcon />}
        </MetaWrapStyled>
      )}
    </RootStyled>
  );
};
